import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ChapterEmblem } from "@/lib/brand";
import LockedChapter from "@/components/LockedChapter";
import PageMeta from "@/components/PageMeta";
import { Lock } from "lucide-react";

export default function Chapters() {
  const [chapters, setChapters] = useState([]);
  const [productsByChapter, setProductsByChapter] = useState({});
  useEffect(() => {
    api.get("/chapters").then(r => setChapters(r.data));
    api.get("/products").then(r => {
      const g = {};
      for (const p of r.data) if (p.chapter_roman) (g[p.chapter_roman] ||= []).push(p);
      setProductsByChapter(g);
    });
  }, []);

  return (
    <div className="min-h-screen pt-32 pb-24" style={{ background: "#f4ecd8" }}>
      <PageMeta title="The Eleven Chapters" description="LOOXS is structured across eleven chapters — Premium Denim, Crew Tees, Ribbed Tanks, Overshirts, Polo Tees, Tailored Trousers, Blazers, Premium Footwear, Leather Belts, Luxury Handbags & Wallets, Signature Eyewear. Each with its own material, mood and ambassador." />
      <div className="max-w-[1400px] mx-auto px-8 md:px-16">
        <div className="text-center mb-24">
          <p className="text-[10px] tracking-luxury uppercase text-goldDark mb-6">Discover</p>
          <h1 className="font-editorial text-5xl md:text-8xl leading-[0.95] tracking-tight">The Chapters</h1>
          <div className="rule-gold w-32 mx-auto my-10" />
          <p className="max-w-2xl mx-auto text-ink/70 leading-relaxed">
            Eleven chapters. One identity. Every category is a considered study in silhouette, fabric and mood — from Premium Denim to Signature Eyewear.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px" style={{ background: "rgba(43,31,20,0.14)" }}>
          {chapters.map((c, i) => {
            const products = productsByChapter[c.roman] || [];
            const hasFaces = !!(c.male_face && c.female_face);
            const hasArt = !hasFaces && c.revealed && !c.restricted && products.length > 0;
            const revealedNoArt = !hasFaces && c.revealed && !c.restricted && products.length === 0;
            return (
              <Link
                key={c.roman}
                data-testid={`chapter-tile-${c.roman}`}
                to={`/chapters/${c.roman}`}
                className="group relative aspect-[4/5] overflow-hidden hover-shift animate-fade-up"
                style={{ animationDelay: `${i * 40}ms`, background: "#efe7d3" }}
              >
                {hasFaces ? (
                  <>
                    <div className="absolute inset-0 grid grid-cols-2">
                      <img src={c.male_face} alt={`Chapter ${c.roman} — ${c.name} · Him`} className="w-full h-full object-cover opacity-95 group-hover:opacity-100 group-hover:scale-[1.02] transition-all duration-1000" />
                      <img src={c.female_face} alt={`Chapter ${c.roman} — ${c.name} · Her`} className="w-full h-full object-cover opacity-95 group-hover:opacity-100 group-hover:scale-[1.02] transition-all duration-1000" />
                    </div>
                    <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(43,31,20,0.55) 0%, rgba(43,31,20,0.08) 45%, transparent 100%)" }} />
                    <div className="absolute inset-0 flex flex-col justify-between p-8 z-10 text-ivory">
                      <div className="flex items-start justify-between">
                        <p className="text-[10px] tracking-widest-plus uppercase opacity-85">Chapter</p>
                        <p className="text-[10px] tracking-widest-plus uppercase opacity-70">Him · Her</p>
                      </div>
                      <div>
                        <ChapterEmblem roman={c.roman} size={52} stroke="#f4ecd8" />
                        <p className="font-editorial text-6xl md:text-7xl mt-4 leading-none">{c.roman}</p>
                      </div>
                      <div>
                        <p className="font-editorial text-2xl">{c.name}</p>
                        {c.tagline && <p className="mt-1 font-editorial-italic opacity-85 text-sm">{c.tagline}</p>}
                        {c.material_language && <p className="mt-3 text-[10px] tracking-widest-plus uppercase opacity-70">{c.material_language}</p>}
                      </div>
                    </div>
                  </>
                ) : hasArt ? (
                  <>
                    <img src={products[0].images?.[0]} alt={`Chapter ${c.roman} — ${c.name}`} className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity duration-1000" />
                    <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(43,31,20,0.35) 0%, rgba(43,31,20,0.02) 55%, transparent 100%)" }} />
                    <div className="absolute inset-0 flex flex-col justify-between p-8 z-10 text-ivory">
                      <div className="flex items-start justify-between">
                        <p className="text-[10px] tracking-widest-plus uppercase opacity-80">Chapter</p>
                      </div>
                      <div>
                        <ChapterEmblem roman={c.roman} size={56} stroke="#f4ecd8" />
                        <p className="font-editorial text-7xl md:text-8xl mt-6 leading-none">{c.roman}</p>
                      </div>
                      <div>
                        <p className="font-editorial text-2xl">{c.name}</p>
                        {c.tagline && <p className="mt-1 font-editorial-italic opacity-85 text-sm">{c.tagline}</p>}
                        {c.material_language && <p className="mt-3 text-[10px] tracking-widest-plus uppercase opacity-70">{c.material_language}</p>}
                      </div>
                    </div>
                  </>
                ) : revealedNoArt ? (
                  // Revealed but no artwork yet — cream editorial placeholder (no lock, no invented image)
                  <div className="relative w-full h-full">
                    <LockedChapter roman={c.roman} showLabel={false} />
                    <div className="absolute inset-0 flex flex-col justify-end p-8 z-10 text-[#5a4526]">
                      <p className="font-editorial text-2xl">{c.name}</p>
                      {c.tagline && <p className="mt-1 font-editorial-italic opacity-80 text-sm">{c.tagline}</p>}
                      {c.material_language && <p className="mt-3 text-[10px] tracking-widest-plus uppercase opacity-65">{c.material_language}</p>}
                    </div>
                  </div>
                ) : (
                  <>
                    <LockedChapter roman={c.roman} restricted={c.restricted} />
                    {c.restricted && (
                      <div className="absolute top-8 right-8 z-10 flex items-center gap-2 text-[#8b6f3d]">
                        <Lock className="w-3 h-3" strokeWidth={1.25} />
                        <span className="text-[10px] tracking-widest-plus uppercase">Founder</span>
                      </div>
                    )}
                  </>
                )}
              </Link>
            );
          })}
        </div>

        <div className="mt-16 text-center text-[10px] tracking-widest-plus uppercase text-ink/40">
          Eleven chapters · One identity · London Wear
        </div>
      </div>
    </div>
  );
}
